import { Responses } from 'constants/responses';
import type { InstagramAudioErrorCode } from '../types';

const INSTAGRAM_AUDIO_ERROR_CODES: InstagramAudioErrorCode[] = [
  'token_unavailable',
  'not_business_account',
  'remote_unavailable',
];

const PROFILE_CONNECTION_CODES: InstagramAudioErrorCode[] = [
  'token_unavailable',
  'not_business_account',
];

type ErrorResponse = {
  status?: number;
  data?: { error_code?: string };
};

/**
 * Only what these helpers read, so `ApiClientError` stays structurally
 * assignable without any of its callers, or the tests, having to build one.
 */
export type InstagramAudioApiError = {
  response?: ErrorResponse;
  axiosError?: { response?: ErrorResponse };
};

/**
 * The client builds `ApiClientError` two different ways: the axios interceptor
 * keeps the original error under `axiosError`, while its own rejection path
 * (`rejectResponse`) puts the response on `response` and leaves `axiosError`
 * undefined. Every non-2xx from this feature takes the second path, so reading
 * only one of them misses the response entirely.
 */
function getErrorResponse(
  error: InstagramAudioApiError | null | undefined,
): ErrorResponse | undefined {
  return error?.response ?? error?.axiosError?.response;
}

export function getInstagramAudioErrorCode(
  error: InstagramAudioApiError | null | undefined,
): InstagramAudioErrorCode | null {
  const errorCode = getErrorResponse(error)?.data?.error_code;

  return INSTAGRAM_AUDIO_ERROR_CODES.find((code) => code === errorCode) ?? null;
}

export function isProfileConnectionError(
  code: InstagramAudioErrorCode | null,
): boolean {
  return !!code && PROFILE_CONNECTION_CODES.includes(code);
}

export function isTrackGone(
  error: InstagramAudioApiError | null | undefined,
): boolean {
  return getErrorResponse(error)?.status === Responses.ERROR.NOT_FOUND;
}
